import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import type { CardSummary } from '../api/client.ts';

import type { Phase } from './auto-activation.ts';

// Cards in PENDING / pre-activation / shipping states resolve to a
// terminal state upstream without any client action — webhook from
// the provider flips status, but the FE has no push channel. A short
// poll while any card is in flight closes the "Plastic on the way"
// chip without requiring the user to navigate away and back. Once
// every card has settled the function returns false and TanStack
// Query disables the interval.
const PENDING_POLL_MS = 30_000;

// Idle-foreground ceiling. The mini-app may sit open in the WebView for
// hours after the user has stopped interacting; a 5-min refetch lets a
// freshly delivered card surface inside the L1 ceiling's slack window
// without hammering the bot every 30 seconds.
const IDLE_FOREGROUND_POLL_MS = 300_000;

const TRANSIENT_STATUSES = new Set(['PENDING', 'CREATING', 'SHIPPED']);

export interface OrchestratorPhaseSnapshot {
  readonly anyWorking: boolean;
  readonly anyWaitingRetry: boolean;
}

const NO_ORCHESTRATOR: OrchestratorPhaseSnapshot = {
  anyWorking: false,
  anyWaitingRetry: false,
};

export const cardsRefetchInterval = (
  cards: readonly CardSummary[] | undefined,
  orchestrator: OrchestratorPhaseSnapshot = NO_ORCHESTRATOR,
): number | false => {
  // A working handshake wants tight polling so the success transition
  // is observed within seconds.
  if (orchestrator.anyWorking) return PENDING_POLL_MS;
  // During waiting_retry the back-off is local to the hook; a card poll
  // would risk a stale "DELIVERED, busy" frame flicker. Pause cleanly.
  if (orchestrator.anyWaitingRetry) return false;
  if (cards === undefined || cards.length === 0) return false;
  const anyTransient = cards.some((c) => TRANSIENT_STATUSES.has((c.status ?? '').toUpperCase()));
  if (anyTransient) return PENDING_POLL_MS;
  // Idle foreground: cards settled, no orchestrator activity. A user with
  // the mini-app still open should eventually see a freshly delivered
  // card without manually re-opening.
  return IDLE_FOREGROUND_POLL_MS;
};

// Phase publish/subscribe via TanStack Query's cache. The orchestrator
// writes its per-card phase under a stable query key (no fetcher
// attached). useOrchestratorPhases reads all entries and aggregates.
// One-direction coupling: orchestrator writes → refetch reads.
const PHASE_QUERY_PREFIX = 'autoActivation' as const;

export const phaseQueryKey = (cardId: string): readonly [string, string, 'phase'] => [
  PHASE_QUERY_PREFIX,
  cardId,
  'phase',
];

export const publishPhase = (qc: QueryClient, cardId: string, phase: Phase): void => {
  // Skip no-op writes. setQueryData fires a cache event even when the
  // new value is structurally equal to the old one. Paired with the
  // snapshot subscriber below, a no-op write would loop: write -> cache
  // event -> setSnapshot(new ref) -> CardsPage re-render -> useQuery
  // re-evaluates refetchInterval -> observer update -> cache event ...
  const current = qc.getQueryData<Phase>(phaseQueryKey(cardId));
  if (current === phase) return;
  qc.setQueryData(phaseQueryKey(cardId), phase);
};

export const useOrchestratorPhases = (): OrchestratorPhaseSnapshot => {
  const qc = useQueryClient();
  const [snapshot, setSnapshot] = useState<OrchestratorPhaseSnapshot>(NO_ORCHESTRATOR);
  useEffect(() => {
    const compute = (): OrchestratorPhaseSnapshot => {
      const entries = qc.getQueriesData<Phase>({ queryKey: [PHASE_QUERY_PREFIX] });
      let anyWorking = false;
      let anyWaitingRetry = false;
      for (const [, value] of entries) {
        if (value === 'working') anyWorking = true;
        if (value === 'waiting_retry') anyWaitingRetry = true;
      }
      return { anyWorking, anyWaitingRetry };
    };
    // Subscriber fires on every cache event across all queries (including
    // ['cards'] refetches). Without the value-equality check setSnapshot
    // would emit a fresh reference each tick, forcing CardsPage to
    // re-render and feeding the loop described in publishPhase.
    const next = compute();
    setSnapshot((prev) =>
      prev.anyWorking === next.anyWorking && prev.anyWaitingRetry === next.anyWaitingRetry
        ? prev
        : next,
    );
    const unsub = qc.getQueryCache().subscribe(() => {
      const n = compute();
      setSnapshot((prev) =>
        prev.anyWorking === n.anyWorking && prev.anyWaitingRetry === n.anyWaitingRetry
          ? prev
          : n,
      );
    });
    return unsub;
  }, [qc]);
  return snapshot;
};
